import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Button,
    Paper,
    Stack,
    Group,
    Text,
    Badge,
    ActionIcon,
    Tooltip,
    Modal,
    TextInput,
    Loader
} from '@mantine/core';
import {
    IconBrandGoogle,
    IconMail,
    IconTrash,
    IconPlus,
    IconInfoCircle,
    IconArrowLeft,
    IconCalendar,
    IconRefresh
} from '@tabler/icons-react';
import PageHeader from '@/components/page-header';
import SettingsLayout from '@/components/settings-layout';
import SettingsCard from '@/components/settings-card';
import { setWebTitle, formatDayLong } from '@/lib/format-utils';
import {
    useGoogleAccounts,
    useDeleteGoogleAccount,
    useGoogleAuthCallback
} from '@/hooks/api-hooks';
import api from '@/services/api-service';
import { notify } from '@/lib/notify';
import { SETTINGS_PATH, SETTINGS_GMAIL_PATH } from '@/lib/navigation';

export default function SettingsGmailPage(): JSX.Element {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    setWebTitle(t('gmail_link'));

    const { data: accountsData, isLoading, refetch } = useGoogleAccounts();
    const accounts = (accountsData as any)?.data || [];

    const deleteAccountMutation = useDeleteGoogleAccount();
    const callbackMutation = useGoogleAuthCallback();
    const handledCodeRef = useRef<string | null>(null);

    const [accountToDelete, setAccountToDelete] = useState<any | null>(null);
    const [connectingOAuth, setConnectingOAuth] = useState(false);

    // Detect OAuth callback code in URL
    useEffect(() => {
        const code = searchParams.get('code');
        const error = searchParams.get('error');

        if (error) {
            setSearchParams({}, { replace: true });
            return;
        }

        if (!code || handledCodeRef.current === code) return;
        handledCodeRef.current = code;

        // Immediately clear code from URL to prevent duplicate execution during network latency
        setSearchParams({}, { replace: true });

        const redirectUri = `${window.location.origin}${SETTINGS_GMAIL_PATH}`;
        callbackMutation.mutate({ code, redirectUri }, {
            onSuccess: () => {
                refetch();
            }
        });
    }, [searchParams]);

    const handleConnectGoogle = async (loginHint?: string) => {
        setConnectingOAuth(true);
        try {
            const redirectUri = `${window.location.origin}${SETTINGS_GMAIL_PATH}`;
            const { data } = await api.googleIntegration.getAuthUrl(redirectUri, loginHint);
            if (data?.url) {
                window.location.href = data.url;
            }
        } catch (err: any) {
            notify.error(err?.message || t('operation_error'), t('error'));
            setConnectingOAuth(false);
        }
    };

    const handleConfirmDelete = () => {
        if (!accountToDelete) return;
        deleteAccountMutation.mutate(accountToDelete.id, {
            onSuccess: () => {
                setAccountToDelete(null);
                refetch();
            },
            onError: () => {
                setAccountToDelete(null);
            }
        });
    };

    return (
        <div className="panel-content max-w-5xl">
            <div className="flex items-center gap-3 mb-2">
                <Button
                    variant="subtle"
                    color="gray"
                    size="xs"
                    leftSection={<IconArrowLeft size={16} />}
                    onClick={() => navigate(SETTINGS_PATH)}
                >
                    {t('settings')}
                </Button>
            </div>

            <PageHeader title={t('gmail_link')} />

            <SettingsLayout>
                <div className="flex flex-col gap-6 w-full mt-4">
                    {/* Informational Callout */}
                    <div className="bg-blue-50/80 border border-blue-200/80 rounded-xl p-4 flex items-start gap-3 text-blue-950">
                        <div className="p-2 rounded-lg bg-blue-100/80 text-blue-700 shrink-0">
                            <IconInfoCircle size={20} />
                        </div>
                        <div className="text-xs leading-relaxed">
                            <div className="font-bold text-sm text-blue-900 mb-0.5">
                                {t('gmail_link')}
                            </div>
                            <div>
                                {t('gmail_link_desc')}
                            </div>
                        </div>
                    </div>

                    {/* Connected Gmail Accounts Card */}
                    <SettingsCard
                        title={t('connected_gmail_accounts')}
                        description={t('gmail_accounts_section_desc')}
                    >
                        <div className="space-y-4">
                            {isLoading ? (
                                <div className="py-8 flex justify-center items-center">
                                    <Loader size="sm" color="blue" />
                                </div>
                            ) : accounts.length === 0 ? (
                                <div className="p-8 text-center bg-gray-50 border border-dashed border-gray-200 rounded-xl">
                                    <div className="w-12 h-12 mx-auto rounded-full bg-red-50 text-red-500 flex items-center justify-center mb-3">
                                        <IconBrandGoogle size={24} />
                                    </div>
                                    <Text className="text-sm font-semibold text-gray-800 mb-1">
                                        {t('no_gmail_accounts_connected')}
                                    </Text>
                                    <Text className="text-xs text-gray-500 max-w-md mx-auto mb-4">
                                        {t('connect_gmail_account_help')}
                                    </Text>
                                    <Button
                                        color="dark"
                                        size="sm"
                                        leftSection={<IconBrandGoogle size={16} />}
                                        onClick={() => handleConnectGoogle()}
                                        loading={connectingOAuth}
                                    >
                                        {t('connect_gmail_account')}
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden bg-white">
                                        {accounts.map((acc: any) => (
                                            <div
                                                key={acc.id}
                                                className="p-4 flex items-center justify-between hover:bg-slate-50/70 transition-colors"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-9 h-9 rounded-lg ${(acc.is_expired || acc.has_required_scopes === false) ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'} flex items-center justify-center shrink-0`}>
                                                        <IconMail size={20} />
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-bold text-sm text-gray-900">
                                                                {acc.google_email}
                                                            </span>
                                                            {acc.has_required_scopes === false ? (
                                                                <Badge size="xs" color="amber" variant="light">
                                                                    {t('status_insufficient_permissions')}
                                                                </Badge>
                                                            ) : acc.is_expired ? (
                                                                <Badge size="xs" color="amber" variant="light">
                                                                    {t('status_expired')}
                                                                </Badge>
                                                            ) : (
                                                                <Badge size="xs" color="teal" variant="light">
                                                                    {t('status_active')}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        {acc.created_at && (
                                                            <div className="flex items-center gap-1 text-[11px] text-gray-400 mt-0.5">
                                                                <IconCalendar size={12} />
                                                                <span>{formatDayLong(acc.created_at)}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <Group className="gap-2">
                                                    {(acc.is_expired || acc.has_required_scopes === false) && (
                                                        <Button
                                                            size="xs"
                                                            variant="light"
                                                            color="orange"
                                                            leftSection={<IconRefresh size={14} />}
                                                            onClick={() => handleConnectGoogle(acc.google_email)}
                                                            loading={connectingOAuth}
                                                        >
                                                            {t('reconnect')}
                                                        </Button>
                                                    )}
                                                    <Tooltip label={t('disconnect')}>
                                                        <ActionIcon
                                                            color="red"
                                                            variant="subtle"
                                                            size="md"
                                                            onClick={() => setAccountToDelete(acc)}
                                                        >
                                                            <IconTrash size={18} />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                </Group>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Action row to add more accounts */}
                                    <div className="pt-2 flex flex-wrap gap-2">
                                        <Button
                                            color="dark"
                                            size="sm"
                                            leftSection={<IconPlus size={16} />}
                                            onClick={() => handleConnectGoogle()}
                                            loading={connectingOAuth}
                                        >
                                            {t('connect_another_gmail_account')}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </SettingsCard>
                </div>
            </SettingsLayout>

            {/* Confirm Disconnect Modal */}
            <Modal
                opened={accountToDelete !== null}
                onClose={() => setAccountToDelete(null)}
                title={<span className="font-bold text-sm">{t('disconnect_google_account')}</span>}
                centered
                size="sm"
            >
                <Stack className="gap-4">
                    <Text className="text-xs text-gray-600 leading-relaxed">
                        {t('disconnect_gmail_account_confirm', { email: accountToDelete?.google_email })}
                    </Text>
                    <Group justify="flex-end" className="gap-2">
                        <Button
                            variant="default"
                            size="xs"
                            onClick={() => setAccountToDelete(null)}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            color="red"
                            size="xs"
                            onClick={handleConfirmDelete}
                            loading={deleteAccountMutation.isPending}
                        >
                            {t('disconnect')}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </div>
    );
}
